import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { 
    PageHeader, 
    PageSection, 
    SecondaryCallout,
    RelatedContent, 
    CrossLink, 
    IndicationPageIntro, 
    AnchorLinks,
    PrimaryClaim,
    KMCurve,
    FeaturedStat,
    ComparisonTable,
    PageSubSection,
    PatientProfile,
    ReferencesBlock,
    TextBlock
} from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import { jobCodes } from '../../util/data/jobCodes';
import NSCLC_NEOADJUVANT from '../../components/StudyDesign/__study-design/nsclc-neoadjuvant-therapy';

// SEO INFORMATION 
const pageMeta = {
    title: `KEYNOTE-671 Clinical Trial Results`,
    keywords: `keynote-671, neoadjuvant therapy followed by adjuvant therapy for nsclc`,
    description: `Health care professionals may find KEYNOTE-671 clinical trial results for neoadjuvant combination therapy followed by adjuvant monotherapy for after surgery patients with resectable (T ≥4 cm or node positive) NSCLC.`,
    schemaJsonLD: [
                `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-neoadjuvant-therapy/","@type":"ApprovedIndication","name":"KEYTRUDA®","alternateName":"pembrolizumab","description":"KEYTRUDA is indicated for the treatment of patients with resectable (tumors ≥4 cm or node positive) non–small cell lung cancer (NSCLC) in combination with platinum-containing chemotherapy as neoadjuvant treatment, and then continued as a single agent as adjuvant treatment after surgery."}`,
                `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-neoadjuvant-therapy/","@type":"MedicalTrial","name":"KEYNOTE-671","description":"The Only IO to Demonstrate a Statistically Significant Improvement in Overall Survival in Resectable (T ≥4 cm or N+), Early-Stage NSCLC vs placebo in combination with platinum-containing chemo followed by placebo alone","trialDesign":"KEYNOTE-671: A randomized, double-blind, multicenter, placebo-controlled, phase 3 study in patients with resectable stage II, IIIA, or IIIB (N2) NSCLC per AJCC 8th edition2"}`,
                `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/nsclc-neoadjuvant-therapy/","@type":"MedicalAudience","audienceType":"health care professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`
     ]
}

//from /src/util/data/indications.js
const indicationId = 34; 
const jobCode = jobCodes[4].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Clinical Data",
            items: [
                { text: '1L Combo Therapy – Nonsquamous ', url: '/efficacy/nsclc-nonsquamous-first-line-combination-therapy' },
                { text: '1L Combo Therapy – Squamous ', url: '/efficacy/nsclc-squamous-first-line-combination-therapy' },
                { text: '1L Monotherapy – Nonsquamous & Squamous', url: '/efficacy/nsclc-first-line-monotherapy' },
                { text: '2L Monotherapy – Nonsquamous & Squamous', url: '/efficacy/nsclc-second-line-monotherapy' },
                { text: 'Adjuvant Therapy for Stage IB (T2a ≥4 cm), Stage II, or Stage IIIA NSCLC', url: '/efficacy/nsclc-adjuvant-therapy' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/nsclc-neoadjuvant-therapy' },
                { text: 'Dosing', url: '/dosing/options/' },
                { text: 'PD&#8288;-&#8288;L1 Testing & Scoring', url: '/biomarker-testing/pd-l1/' },
            ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const featuredStatFootnotesTwo = [
  {
    label: 'a.',
    text: 'Chemotherapy consisted of cisplatin 75 mg/m<sup>2</sup> IV on day 1 + (pemetrexed 500 mg/m<sup>2</sup> IV on day 1 or gemcitabine 1,000 mg/m<sup>2</sup> IV on days 1 and 8) of each 21-day cycle for up to 4 cycles.'
  },
  {
    label: 'b.',
    text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD&#8288;-&#8288;L1 expression, histology, and geographic region.'
  },
  {
    label: 'c.',
    text: '<i>P</i> value based on stratified log-rank test.'
  },
  {
    label: 'd.',
    text: 'Compared to a two-sided <i>P</i> value boundary of 0.0109.'
  },
  
  {
    label: 'e.',
    text: 'Median OS based on Kaplan-Meier estimates.'
  }
]

const pathologicalRrFootnotes = [
    {
        label: 'f.',
        text: 'HR based on Cox regression model with treatment as a covariate stratified by stage, tumor PD&#8288;-&#8288;L1 expression, histology, and geographic region.'
    },
    {
        label: 'g.',
        text: '<i>P</i> value based on stratified log-rank test.'
    },
    {
        label: 'h.',
        text: 'Compared to a two-sided <i>P</i> value boundary of 0.0092.'
    },
    {
        label: 'i.',
        text: 'Median EFS based on Kaplan-Meier estimates.'
    }
]

const patientProfileJess = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/jess.png" placeholder="TRACED_SVG" alt="..." width={100} height={100} />
    ),
    name: "Jess",
    diagnosis: "Stage IIB<sup>o</sup> and T2bN1M0 nonsquamous NSCLC",
    primaryCharacteristics: [
        {
            key: 'Treatment History',
            value: 'No prior history of therapy',
            emphasize: false,
        },
        {
            key: 'Imaging Exam',
            value: '4.8-cm mass with ipsilateral hilar lymph node involvement',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
        {
            key: 'PD&#8288;-&#8288;L1 Expression ',
            value: 'TPS 65%',
            emphasize: false,
        },     
    ],
    secondaryCharacteristics: [
        {
            key: 'Age',
            value: '58',
        },
        {
            key: 'Gender',
            value: 'Female',
        },
        {
            key: 'Physical Activity',
            value: 'Moderate',
        },
    ],
}


const patientProfilePablo = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/pablo.png" placeholder="TRACED_SVG" alt="..." width={100} height={100} />
    ),
    name: "Pablo",
    diagnosis: "Stage IIIA<sup>o</sup> and T1cN2M0 nonsquamous NSCLC",
    primaryCharacteristics: [
        {
            key: 'Treatment History',
            value: 'No prior history of therapy',
            emphasize: false,
        },
        {
            key: 'Imaging Exam',
            value: '2.9-cm mass with ipsilateral hilar and paratracheal lymph node involvement',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '1',
            emphasize: false,
        },
        {
            key: 'PD&#8288;-&#8288;L1 Expression ',
            value: 'TPS 0%',
            emphasize: false,
        },     
    ],
    secondaryCharacteristics: [
        {
            key: 'Age',
            value: '73',
        },
        {
            key: 'Gender',
            value: 'Male',
        },
        {
            key: 'Physical Activity',
            value: 'Low',
        },
    ],
    footnotes: [
        {
            label: 'o.',
            text: 'Per AJCC 8th ed.'
        }
    ]
}

const pageReferences = [
    {
        label: "1.",
        text: "Data available on request from Merck, Professional Services-DAP, WP1-27, PO Box 4, West Point, PA 19486-0004. Please specify information package US-LAM-03645."
    },
    {
        label: "2.",
        text: "Wakelee H, Liberman M, Kato T, et al. Perioperative pembrolizumab for early-stage non-small-cell lung cancer. <i>N Engl J Med.</i> Published online June 3, 2023. doi:10.1056/NEJMoa2302983"
    },
    
]

const anchorLinkModelData = [
    {label: 'Clinical Findings From KEYNOTE&#8288;-&#8288;671'},
    {label: 'Study Design for KEYNOTE&#8288;-&#8288;671'},
    {label: 'Appropriate Patients'},
];

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location }>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results" />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                        <AnchorLinks items={anchorLinkModelData} />
                   </PageSection>
                   <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">
                            <strong>The Only IO to Demonstrate a Statistically Significant Improvement in Overall Survival in Resectable </strong>(T ≥4 cm or N+), <strong>Early-Stage NSCLC</strong> vs placebo in combination with platinum-containing chemo followed by placebo alone                    
                        </PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection title={anchorLinkModelData[0].label} bgColor="cloud">
                        <PageSubSection title="SECOND INTERIM ANALYSIS (median follow-up time: 36.6 months)<sup>1</sup>">
                            <SecondaryCallout
                                title='Statistically significant improvement in OS with KEYTRUDA + chemotherapy/KEYTRUDA vs placebo + chemotherapy/placebo<sup>a</sup>'
                                alt=""
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates of OS in KEYNOTE&#8288;-&#8288;671"
                                hr="HR=0.72; 95% CI, 0.56—0.93; <i>P</i>=0.0103<sup>b-d</sup>"
                                definitions='T = tumor size; N+ = node positive; OS = overall survival; HR =  hazard ratio; CI = confidence interval.'
                                image={() => <StaticImage src="../../assets/nsclc-km-kn-671-v2.webp"
                                    placeholder="none"
                                    alt="Kaplan-Meier Estimates of Overall Survival (OS) in KEYNOTE-671" />
                                }
                            />
                            <FeaturedStat
                                statistic="28%"
                                title="Reduction in the risk of death with KEYTRUDA + chemotherapy/KEYTRUDA vs placebo + chemotherapy/placebo"
                                hr="(HR=0.72; 95% CI, 0.56&ndash;0.93; <i>P</i>=0.0103)<sup>b-d</sup>"
                            />
                            <ComparisonTable 
                                title="Overall Survival (OS)" 
                                footnotes={featuredStatFootnotesTwo}
                                definitions="NR = not reached; PD-L1 = programmed death ligand 1; IV = intravenous."
                                data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                        },
                                        {
                                            legendTitle: 'Placebo + chemo/placebo (n=400)',
                                        },
                                    ],
                                    [
                                        {
                                            colspan: 2,
                                            centerText: true,
                                            hrCiP: 'HR=0.72 (95% CI, 0.56–0.93; <i>P</i>=0.0103)<sup>b-d</sup>'
                                        }
                                    ],
                                    [
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '110',
                                            dataSubPoint: '(28%)'
                                        },
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '144',
                                            dataSubPoint: '(36%)'
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median OS<sup>e</sup>',
                                            dataPoint: 'NR',
                                            dataSubPoint: '(95% CI, NR–NR)'
                                        },
                                        {
                                            label: 'Median OS<sup>e</sup>',
                                            dataPoint: '52.4 months',
                                            dataSubPoint: '(95% CI, 45.7–NR)'
                                        },
                                    ],
                                ]
                            } />
                        </PageSubSection>
                        <PageSubSection title="FIRST INTERIM ANALYSIS (median follow-up time: 25.2 months)<sup>2</sup>">
                            <SecondaryCallout
                                title='Superior event-free survival (EFS) with KEYTRUDA + chemotherapy/KEYTRUDA vs placebo + chemotherapy/placebo'
                                alt=""
                            />

                            <FeaturedStat
                                statistic="42%"
                                title="Reduction in the risk of event (progression, recurrence, or death) with KEYTRUDA + chemotherapy/KEYTRUDA vs placebo + chemotherapy/placebo"
                                hr="(HR=0.58; 95% CI, 0.46&ndash;0.72; <i>P</i><0.0001)<sup>2,f-h</sup>"
                            />
                            <ComparisonTable 
                                title="Event-Free Survival" 
                                data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + chemo/KEYTRUDA (n=397)',
                                        },
                                        {
                                            legendTitle: 'Placebo + chemo/placebo (n=400)',
                                        },
                                    ],
                                    [
                                        {
                                            colspan: 2,
                                            centerText: true,
                                            hrCiP: 'HR=0.58 (95% CI, 0.46–0.72; <i>P</i><0.0001)<sup>f-h</sup>'
                                        }
                                    ],
                                    [
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '139',
                                            dataSubPoint: '(35%)'
                                        },
                                        {
                                            label: 'Events Observed',
                                            dataPoint: '205',
                                            dataSubPoint: '(51%)'
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median EFS<sup>i</sup>',
                                            dataPoint: 'NR',
                                            dataSubPoint: '(95% CI, 34.1–NR)'
                                        },
                                        {
                                            label: 'Median EFS<sup>i</sup>',
                                            dataPoint: '17.0 months',
                                            dataSubPoint: '(95% CI, 14.3–22.0)'
                                        },
                                    ],
                                ]
                            } />
                        </PageSubSection>
                        
                        <PageSubSection title="Pathological Response Rates">
                                <TextBlock footnotes={pathologicalRrFootnotes}>
                                    <p>The trial demonstrated a statistically significant difference in pathological complete response (pCR) rate (18.1% vs 4.0%; <i>P</i>&lt;0.0001) and major pathological response (mPR) rate (30.2% vs 11.0%; <i>P</i>&lt;0.0001).</p>
                                </TextBlock>
                            </PageSubSection>
                        </PageSection>

                        <PageSection bgColor='white'>
                            <PageSubSection title={anchorLinkModelData[1].label}>
                                <NSCLC_NEOADJUVANT />
                            </PageSubSection>
                        </PageSection>

                        <PageSection
                            bgColor="cloud"
                            title={anchorLinkModelData[2].label}
                        >
                            <PageSubSection
                                title='Hypothetical Patients Based on Those Studied in KEYNOTE&#8288;-&#8288;671'
                            >
                                <PatientProfile {...patientProfileJess} />
                                <PatientProfile {...patientProfilePablo} />
                            

                                <SecondaryCallout 
                                    title="For your eligible patients with resectable (tumors ≥4 cm or node positive) NSCLC, start with KEYTRUDA in combination with platinum-containing chemotherapy in the neoadjuvant setting. Stay with KEYTRUDA as a single agent in the adjuvant setting after surgery."
                                    alt="Start KEYTRUDA® (pembrolizumab) With Chemotherapy in the Neoadjuvant Setting for High-Risk Early-Stage Triple-Negative Breast Cancer (TNBC) and Stay With KEYTRUDA® (pembrolizumab) as a Single Agent in the Adjuvant Setting"
                                />
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>
                
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
